import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import clsx from "clsx";
import { forwardRef, memo } from "react";
import { useTheme } from "./ThemeProvider";
import { mergeSx, resolveCss, } from "../system";
export const clickBaseClasses = {
    root: "MinervaClickBase-root",
    disabled: "MinervaClickBase-disabled",
    readonly: "MinervaClickBase-readonly",
};
/**
 * `ClickBase` is a foundational component that standardizes basic click handling functionality. It is used as a base for clickable components, such as buttons or links.
 *
 * It also implements the polymorphic component pattern seen in the base component. This allows it to be extended to things like inputs or any other html element.
 */
export const ClickBase = memo(forwardRef(({ as, disabled, focused, readonly, disableHoverEffect, disableFocusEffect, disableActiveEffect, sx, className, ...rest }, ref) => {
    const Component = as || "button";
    //Used during the mui theme deprecation because of the conflicting react context
    const theme = useTheme();
    const focusedColor = "rgba(0, 0, 0, 0.05)";
    const hoveredColor = "rgba(0, 0, 0, 0.075)";
    const activeColor = "rgba(0, 0, 0, 0.10)";
    return (_jsx(Component, { ref: ref, className: clsx([
            clickBaseClasses.root,
            disabled && clickBaseClasses.disabled,
            readonly && clickBaseClasses.readonly,
            className,
        ]), "aria-disabled": disabled, disabled: disabled, css: resolveCss({
            theme,
            sx: mergeSx({
                position: "relative",
                background: "none",
                border: "none",
                cursor: "pointer",
                pointerEvents: "auto",
                width: "fit-content",
                textDecoration: "none",
                minWidth: 0,
                [`&.${clickBaseClasses.root}`]: {
                    boxSizing: "border-box",
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: focused && !disableFocusEffect
                            ? focusedColor
                            : undefined,
                        borderRadius: "inherit",
                        opacity: focused ? 1 : 0,
                        transition: "opacity 0.1s ease-in-out",
                        pointerEvents: "none",
                    },
                    "&:hover": !disableHoverEffect && {
                        "&::after": {
                            background: hoveredColor,
                            opacity: 1,
                        },
                    },
                    "&:active": !disableActiveEffect && {
                        "&::after": {
                            background: activeColor,
                            opacity: 1,
                        },
                    },
                    "&:focus-visible": {
                        outline: 2,
                        outlineOffset: -0.5,
                        outlineColor: "#000000",
                        boxShadow: "inset 0 0 0 4px #ffffff",
                        "&::after": {
                            background: focusedColor,
                            opacity: 1,
                        },
                    },
                    "&:focus:not(:focus-visible)": {
                        outline: 0,
                    },
                },
                [`&.${clickBaseClasses.disabled}`]: {
                    pointerEvents: "none",
                },
                [`&.${clickBaseClasses.readonly}`]: {
                    pointerEvents: "none",
                },
            }, sx),
        }), ...rest }));
}));
//# sourceMappingURL=ClickBase.js.map